//React
import { useState, useMemo } from "react";
import { createPortal } from "react-dom";

//MUI
import { Button, Grid, TextField, Stack, Tooltip } from "@mui/material";
import PinnedFilterMuiMultiSelect from "./QueryBuilder/PinnedFilterMuiMultiSelect";
import StopRoundedIcon from "@mui/icons-material/StopRounded";

//Query Builder
import { AdvancedQueryBuilder, advancedFormatQuery } from "./QueryBuilder/AdvancedQueryBuilder";
import { QuickQueryBuilder } from "./QueryBuilder/QuickQueryBuilder";

//Backend APIs
import { GetAggregateData } from "../API/TrackRecord";

let abortController = null;

function calculateCorrelationToken(
  correlation_token, 
  sql_query, 
  startDate, 
  endDate, 
  reportingCurrency, 
  fxType,
  excludeFxForwards,
  excludeShorts,
  includeVP,
  overrideDate,
  isCashflow
) {

  if (!correlation_token) return; 

  const isDifferentBase =
    correlation_token["query"] !== sql_query ||
    correlation_token["startDate"] !== startDate.toISOString() ||
    correlation_token["endDate"] !== endDate.toISOString() ||
    correlation_token["reportingCurrency"] !== reportingCurrency;

  const isDifferentCashflowExtras = isCashflow && (
    (fxType !== null && correlation_token["fxType"] !== fxType) ||
    (typeof excludeFxForwards === "boolean" && correlation_token["excludeFxForwards"] !== excludeFxForwards) ||
    (typeof excludeShorts === "boolean" && correlation_token["excludeShorts"] !== excludeShorts) ||
    (typeof includeVP === "boolean" && correlation_token["includeVP"] !== includeVP) ||
    (typeof overrideDate === "string" && correlation_token["overrideDate"] !== overrideDate)
  );

  if (isDifferentBase || isDifferentCashflowExtras) {
    sessionStorage.removeItem("correlation_token");
    return null;
  } else {
    return correlation_token["id"];
  }
}

function TrackRecordBuilder(props) {
  const {
    query,
    quickQuery,
    setQuery,
    setQuickQuery,
    setLoader,
    setColumns,
    setRows,
    setRowCount,
    setViewDataGrid,
    setCorrelationIdTracker,
    appBusinessUnit,
    aggregateByFields,
    fields,
    setPnlTip,
    setExpoTip,
    startDate,
    endDate,
    isDateRangeValid,
    aggregate,
    setAggregate,
    reportingCurrency,
    fxType=null,
    excludeFxForwards=null,
    excludeShorts=null,
    includeVP=null,
    overrideDate=null,
    isCashflow,
    isAdvanced,
    runButtonContainerRef,
    setRunError,
  } = props;
  const [cancelBtnVisible, setCancelBtnVisible] = useState(false);
  const [runBtnVisible, setRunBtnVisible] = useState(true);
  const [selectedTimePrd, setSelectedTimePrd] = useState("");
  const [isQueryValid, setIsQueryValid] = useState(false);

  const aggTimePeriods = useMemo(() => {
    return aggregateByFields.filter((item) => item.category === "Time Period").map((item) => item.label);
  }, [aggregateByFields]);

  const disabledTimePeriods = useMemo(() => {
    if (!selectedTimePrd) return [];

    return aggTimePeriods.filter((str) => str !== selectedTimePrd);
  }, [selectedTimePrd, aggTimePeriods]);

  const options = useMemo(() => {
    return aggregateByFields?.filter((item) => !disabledTimePeriods.includes(item.label));
  }, [aggregateByFields, disabledTimePeriods]);

  const isOverrideValid = useMemo(()=>
    fxType !== 'Specific Date' || overrideDate
  ,[fxType, overrideDate]);

  const numberValueFormatter = (params) => {
    // Handle null, undefined, empty string, or whitespace
    if (params.value == null || params.value === '' || (typeof params.value === 'string' && params.value.trim() === '')) {
      return '';
    }
    
    // If already a number
    if (typeof params.value === 'number') {
      if (isNaN(params.value) || !isFinite(params.value)) {
        return params.value.toString(); // Return as string for NaN or Infinity
      }
      const precision = params.field === "ROA" ? 10 : 2;  // Use more decimals for ROA

      return params.value.toLocaleString('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
      });
    }
    // For other types, return as is
    return params.value;
  };

  function getColumns(columnKs) {
    return columnKs
      .filter((val) => val !== "internalId")
      .map((value) => ({
        field: value,
        headerName: value,
        width: 200,
        editable: false,
        valueFormatter: numberValueFormatter
      }));
  }

  async function cancel() {
    console.log("Operation canceled by the user.");
    abortController.abort();
    setLoader(false);
    setRunBtnVisible(true);
    setCancelBtnVisible(false);
  }

  async function onRunClick(sql_query, aggregateby) {
    setRunError?.(null);
    abortController = new AbortController();
    setRowCount(0);
    setRows([]);
    setColumns([]);
    setLoader(true);
    setRunBtnVisible(false);
    setCancelBtnVisible(true);
    setViewDataGrid(true);
    console.log("Query from UI:" + JSON.stringify(sql_query));
    let query_resp;
    const aggregate_by = JSON.stringify(aggregateby);
    let correlation_token = JSON.parse(sessionStorage.getItem("correlation_token"));

    try {
      let correlation_token_id = calculateCorrelationToken(
        correlation_token,
        sql_query,
        startDate,
        endDate,
        reportingCurrency,
        fxType,
        excludeFxForwards,
        excludeShorts,
        includeVP,
        overrideDate,
        isCashflow
      );

      query_resp = await GetAggregateData(
        sql_query,
        startDate,
        endDate,
        reportingCurrency,
        fxType,
        excludeFxForwards,
        excludeShorts,
        includeVP,
        overrideDate,
        appBusinessUnit,
        correlation_token_id,
        aggregate_by,
        abortController,
        isCashflow
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        const devPayload = {
          message: error?.message,
          name: error?.name,
          errorType: error?.errorType,
          detail: error?.detail,
          statusCode: error?.statusCode,
        };
        const m = error?.message || "";
        const unreachable =
          m.includes("Failed to fetch") ||
          m.includes("NetworkError") ||
          m.includes("Load failed");
        if (unreachable) {
          console.error(
            "[scenario: backend-unreachable]",
            "API did not respond (check API_BASE_URL, VPN, CORS).",
            devPayload
          );
        } else if (error?.errorType === "DUCKDB_QUERY_FAILED") {
          console.error(
            "[scenario: parquet-data]",
            "DuckDB/parquet read failed (missing parquet, wrong paths in configuration.ini, or DuckDB SQL error - not SQL Server).",
            devPayload
          );
        } else if (error?.errorType === "DATABASE_UNAVAILABLE") {
          console.error(
            "[scenario: database]",
            "SQL Server unavailable (pyodbc - saved queries / iHub / DW path).",
            devPayload
          );
        } else {
          console.error(
            "[scenario: aggregateQueryRun]",
            "Run failed (see errorType/detail if present).",
            devPayload
          );
        }
        setRunError?.(true);
      }
      setLoader(false);
      setRunBtnVisible(true);
      setCancelBtnVisible(false);
      return;
    }
    const data = query_resp.data;
    correlation_token = query_resp.correlation_token;
    const row_count = query_resp.row_count;
    const pnl_message = query_resp.pnl_message;
    const expo_message = query_resp.expo_message;
    setRowCount(row_count);
    setPnlTip(pnl_message);
    setExpoTip(expo_message);

    setCorrelationIdTracker(
      query_resp.aggregated_file_token === null ? correlation_token : query_resp.aggregated_file_token
    );
    sessionStorage.setItem(
      "correlation_token",
      JSON.stringify({
        id: correlation_token,
        query: sql_query,
        startDate: startDate,
        endDate: endDate,
        reportingCurrency: reportingCurrency,
        fxType: fxType,
        excludeFxForwards: excludeFxForwards,
        excludeShorts: excludeShorts,
        includeVP: includeVP,
        overrideDate: overrideDate,
      })
    );
    const finaldata = data;
    if (
      row_count === 0 ||
      !finaldata ||
      (Array.isArray(finaldata) && finaldata.length === 0)
    ) {
      console.warn(
        "[scenario: zero-rows]",
        "Query completed with 0 rows - check parquet coverage for the date range, query filters, and business unit (data may be empty; this is not a backend-down or DB-connection failure).",
        {
          rowCount: row_count,
          businessUnit: appBusinessUnit,
          isCashflow,
        }
      );
    }
    if (Array.isArray(finaldata) && finaldata.length > 0) {
      const columnKs = Object.keys(finaldata[0]);
      const cols = getColumns(columnKs);
      setColumns(cols);
      setRows(finaldata);
      setLoader(false);
    } else {
      setLoader(false);
    }

    setRunBtnVisible(true);
    setCancelBtnVisible(false);
  }

  // -------------------------------- RENDER -----------------------------------
  return (
    <Stack sx={{ width: 1 }} direction="row" spacing={1}>
      <Grid container sx={{ width: 1 }}>
        <Grid item xs={12} sx={{ marginBottom: 1 }}>
          {isAdvanced ? (
            <AdvancedQueryBuilder {...{ fields, query, setQuery, setIsQueryValid }} />
          ) : (
            <QuickQueryBuilder {...{ fields, quickQuery, setQuickQuery, setIsQueryValid }} />
          )}
        </Grid>

        {/* Aggregate field (only for advanced ROA/positions): strikethrough title on border */}
        {isAdvanced && !isCashflow ? (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <PinnedFilterMuiMultiSelect
              size="small"
              multiple
              loading={aggregateByFields.length === 0}
              options={options}
              getOptionLabel={(option) => option.label}
              value={aggregate}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Group by"
                  placeholder="Start typing the name of the column you want to group by..."
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      "& fieldset": { borderColor: "#b0bec5" },
                      "&:hover fieldset": { borderColor: "#90a4ae" },
                      "&.Mui-focused fieldset": { borderColor: "#78909c", borderWidth: "1px" },
                    },
                    "& .MuiInputLabel-root": { color: "#37474f" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#37474f" },
                  }}
                />
              )}
              groupBy={(option) => option.category}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              onChange={(event, newValue) => {
                setAggregate(newValue);
                let latestTimePrds = newValue.map((item) => item.label).filter((item) => aggTimePeriods.includes(item));
                if (latestTimePrds.length === 0) {
                  setSelectedTimePrd("");
                } else {
                  setSelectedTimePrd(latestTimePrds[0]);
                }
              }}
            />
          </Grid>
        ) : null}
      </Grid>
      {runButtonContainerRef?.current
        ? createPortal(
            <>
              {runBtnVisible && (
                <span
                  onClick={() => {
                    if (!isQueryValid || !isDateRangeValid || !isOverrideValid) {
                      window.dispatchEvent(new CustomEvent("validate-form"));
                    }
                  }}
                >
                <Button
                  id="RunButton"
                  aria-label="Run Track Record Query"
                  variant="contained"
                  disabled={!isQueryValid || !isDateRangeValid || !isOverrideValid}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("clear-validation"));
                    if (isAdvanced) {
                      onRunClick(
                        advancedFormatQuery(query, "SQL"),
                        aggregate.map((item) => item.label)
                      );
                    } else {
                      onRunClick(advancedFormatQuery(quickQuery, "SQL"), {});
                    }
                  }}
                  sx={{
                    backgroundColor: "#0000FF",
                    color: "#fff",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    padding: "8px 24px",
                    minWidth: "100px",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#0015a3",
                    },
                    "&:disabled": {
                      backgroundColor: "#ccc",
                      color: "#666",
                    },
                  }}
                >
                  RUN
                </Button>
                </span>
              )}
              {cancelBtnVisible && (
                <Tooltip title="Cancel">
                  <Button
                    id="CancelButton"
                    variant="contained"
                    onClick={() => cancel()}
                  >
                    <StopRoundedIcon />
                  </Button>
                </Tooltip>
              )}
            </>,
            runButtonContainerRef.current
          )
        : !runButtonContainerRef && (
            <Grid>
              {runBtnVisible && (
                <span
                  onClick={() => {
                    if (!isQueryValid || !isDateRangeValid || !isOverrideValid) {
                      window.dispatchEvent(new CustomEvent("validate-form"));
                    }
                  }}
                >
                <Button
                  id="RunButton"
                  aria-label="Run Track Record Query"
                  variant="contained"
                  disabled={!isQueryValid || !isDateRangeValid || !isOverrideValid}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("clear-validation"));
                    if (isAdvanced) {
                      onRunClick(
                        advancedFormatQuery(query, "SQL"),
                        aggregate.map((item) => item.label)
                      );
                    } else {
                      onRunClick(advancedFormatQuery(quickQuery, "SQL"), {});
                    }
                  }}
                  sx={!isAdvanced ? {
                    backgroundColor: "#0000FF",
                    color: "#fff",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    padding: "8px 24px",
                    minWidth: "100px",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#0015a3",
                    },
                    "&:disabled": {
                      backgroundColor: "#ccc",
                      color: "#666",
                    },
                  } : {}}
                >
                  RUN
                </Button>
                </span>
              )}
              {cancelBtnVisible && (
                <Tooltip title="Cancel">
                  <Button
                    id="CancelButton"
                    variant="contained"
                    onClick={() => cancel()}
                  >
                    <StopRoundedIcon />
                  </Button>
                </Tooltip>
              )}
            </Grid>
          )}
    </Stack>
  );
}

export default TrackRecordBuilder;
